import arriereBackoffice, {
  DiscountParams,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import {
  ItemCancellationInput,
  UpdateCancelledBookingFeesInput,
} from '@new/domains/bookings/stores/_types'

export default {
  addDeposit(bookingId: string, title: string, total: number): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        addDeposit: {
          __args: {
            bookingId,
            title,
            total,
          },
        },
      })
      .then(result => result?.addDeposit)
  },

  cancelItems(bookingId: string, items: string[], reason: string): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        cancelItems: {
          __args: {
            bookingId,
            items,
            reason,
          },
        },
      })
      .then(result => result?.cancelItems)
  },

  cancelDeposits(bookingId: string, input: ItemCancellationInput[]): Promise<string> {
    return arriereBackoffice.mutations
      .folio({
        cancelDeposits: {
          __args: {
            bookingId,
            input,
          },
        },
      })
      .then(result => result?.cancelDeposits)
  },

  // Adds a discount on a particular item
  discountItem(bookingId: string, itemId: string, discount: DiscountParams): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        discountItem: {
          __args: {
            bookingId,
            itemId,
            discount,
          },
        },
      })
      .then(result => result?.discountItem)
  },

  // Adds a refund on a particular item
  adjustItem(
    bookingId: string,
    itemId: string,
    adjustAmount: number,
    reason: string,
  ): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        adjustItem: {
          __args: {
            bookingId,
            itemId,
            adjustAmount,
            reason,
          },
        },
      })
      .then(result => result?.adjustItem)
  },

  // Adds an adjustment item: positive adjustment increases balance, negative adjustment decreases balance
  addAdjustment({
    bookingId,
    amount,
    reason,
  }: {
    bookingId: string
    amount: number
    reason: string
  }): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        addAdjustment: {
          __args: {
            bookingId,
            amount,
            reason,
          },
        },
      })
      .then(result => result?.addAdjustment)
  },

  chargeDeposit(bookingId: string, depositId: string, amount: number): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        chargeDeposit: {
          __args: {
            bookingId,
            depositId,
            amount,
          },
        },
      })
      .then(result => result?.chargeDeposit)
  },

  chargeWallet(bookingId: string, email: string, amount: number): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        chargeWallet: {
          __args: {
            bookingId,
            email,
            amount,
          },
        },
      })
      .then(result => result?.chargeWallet)
  },

  refundDeposits(bookingId: string): Promise<boolean> {
    return arriereBackoffice.mutations
      .folio({
        refundDeposits: {
          __args: {
            bookingId,
          },
        },
      })
      .then(result => result?.refundDeposits)
  },

  sendBillNotification(bookingId: string, email: string, phone: string): Promise<boolean> {
    return arriereBackoffice.mutations
      .booking({
        sendBillNotification: {
          __args: {
            bookingId,
            email,
            phone,
          },
        },
      })
      .then(result => result?.sendBillNotification)
  },

  walletBalance(personId: string): Promise<number> {
    return arriereBackoffice.queries.walletBalance({
      __args: {
        personId,
      },
    })
  },

  checkIfUserExists(email: string): Promise<boolean> {
    return arriereBackoffice.mutations.checkIfUserExists({
      __args: {
        email,
      },
    })
  },

  updateCancelledBookingFees({
    bookingId,
    cancellationFee,
    otaCommissionFee,
  }: UpdateCancelledBookingFeesInput) {
    return arriereBackoffice.mutations
      .folio({
        __alias: 'updateCancelledBookingFees',
        updateCancelledBookingFees: {
          __args: { bookingId, input: { cancellationFee, otaCommissionFee } },
          cancellationFee: { amount: true, itemId: true },
          otaCommissionFee: { amount: true, itemId: true },
          fees: { cancelled: true, title: true, amount: true, itemId: true },
        },
      })
      .then(result => result?.updateCancelledBookingFees)
  },

  cancelPaymentIfAwaiting(paymentId: string): Promise<boolean> {
    return arriereBackoffice.mutations
      .booking({
        cancelPaymentIfAwaiting: { __args: { paymentId } },
      })
      .then(booking => {
        Alert.Success('Transaction payment was successfully canceled!')
        return booking.cancelPaymentIfAwaiting
      })
  },
}
